import { useEffect, useState } from "react";
import { Copy, Trash2, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { OUTPUT_MODES } from "@/lib/brand";
import { downloadText, tsFilename } from "@/lib/download";

export default function HistoryPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/history");
        if (!cancelled) setRows(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const remove = async (id) => { await api.delete(`/history/${id}`); setRows((rows) => rows.filter((r) => r.id !== id)); toast.success("Deleted"); };
  const copy = async (t) => { await navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="px-5 lg:px-10 py-8 max-w-5xl mx-auto">
      <h1 className="text-3xl font-bold text-brand-navy">Dictation history</h1>
      <p className="text-sm text-brand-muted mt-1">Every polished draft you&apos;ve created, ready to copy or revisit.</p>

      {loading ? <div className="mt-8 text-brand-muted">Loading…</div> : rows.length === 0 ? (
        <div className="mt-12 text-center py-16 bg-white rounded-3xl border border-brand-borderc">
          <FileText className="w-12 h-12 mx-auto text-brand-purple" />
          <h3 className="mt-4 text-lg font-semibold text-brand-navy">No drafts yet</h3>
          <p className="text-sm text-brand-muted mt-1">Head to Voice Studio and dictate your first draft.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {rows.map((r) => {
            const mode = OUTPUT_MODES.find((m) => m.id === r.output_mode);
            return (
              <div key={r.id} data-testid={`history-row-${r.id}`} className="bg-white rounded-2xl border border-brand-borderc p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 rounded-full bg-brand-surface text-xs font-semibold text-brand-navy">{mode?.label || r.output_mode}</span>
                    <span className="px-2.5 py-1 rounded-full bg-brand-surface text-xs text-brand-muted uppercase">{r.language}</span>
                    <span className="text-xs text-brand-muted">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => copy(r.polished_text)} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface hover:bg-brand-borderc inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" />Copy</button>
                    <button data-testid={`download-history-${r.id}`} onClick={() => {
                      const parts = [];
                      if (r.polished_text) parts.push(`# ${mode?.label || r.output_mode}\n\n${r.polished_text}`);
                      if (r.raw_text && r.raw_text !== r.polished_text) parts.push(`\n\n# Raw transcript\n\n${r.raw_text}`);
                      downloadText(tsFilename("draftvoice"), parts.join("\n"));
                    }} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface hover:bg-brand-borderc inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" />.txt</button>
                    <button onClick={() => remove(r.id)} className="text-xs px-3 py-1.5 rounded-full text-red-600 bg-red-50 hover:bg-red-100 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Delete</button>
                  </div>
                </div>
                <div className="mt-3 text-sm text-brand-text whitespace-pre-wrap leading-relaxed">{r.polished_text}</div>
                <details className="mt-3 text-xs">
                  <summary className="text-brand-muted cursor-pointer">Show raw transcript</summary>
                  <div className="mt-2 p-3 rounded-lg bg-brand-bg text-brand-muted whitespace-pre-wrap">{r.raw_text}</div>
                </details>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
